import React, { Fragment, useEffect, useMemo, useState } from "react";
import { CheckCircle2 } from "lucide-react";

import { API_OFFBOARDING_ACKNOWLEDGED } from "../../../../../../config/Endpoints.js";
import { useSqeLabs } from "../../../../../hooks/use-sqe-labs.ts";
import { SQE_OFFBOARDING_URL } from "../../api/sqe-offboarding-api.ts";
import type { OffboardingSource } from "../../types.ts";
import { postApi } from "../../../../../utils/api.ts";
import { accessToken } from "../../../../../utils/auth.ts";
import { showError, showSuccess } from "../../../../../utils/toast.ts";
import { Button, Modal, Spinner } from "../../../../ui/index.ts";
import { useOffboardingQuestionnaire } from "../../hooks/use-offboarding-questionnaire.ts";
import { useOffboardingDetail } from "../../hooks/use-offboarding-detail.ts";
import { shouldShowOffboardingQuestion } from "../../utils/offboarding-questionnaire-utils.ts";
import { OffboardingQuestionRenderer } from "../../components/question-renderers.tsx";
import { DeallocatedAssetsTable } from "../../components/allocated-assets-table/allocated-assets-table.tsx";
import type {
  OffboardingQuestion,
  OffboardingSectionType,
} from "../../schemas/index.ts";
import styles from "./view-clearance-form-modal.module.css";

export interface ViewClearanceFormModalProps {
  open: boolean;
  onClose: () => void;
  offboardingId: number;
  source?: OffboardingSource;
  sectionType: OffboardingSectionType;
  title: string;
  isAcknowledged?: boolean;
  onAcknowledgeSuccess?: () => void;
}

type AnsweredQuestion = {
  question: OffboardingQuestion;
  answer: string;
  questionIndex: number;
};

const IT_CLEARANCE_GROUPS = [
  { title: "Email Section", start: 0, end: 3 },
  { title: "Biometric Section", start: 4, end: 5 },
  { title: "Additional Comment", start: 6, end: 6 },
];

const SQE_IT_CLEARANCE_GROUPS = [
  ...IT_CLEARANCE_GROUPS,
  { title: "Company Assets", start: 7, end: Number.POSITIVE_INFINITY },
];

export function ViewClearanceFormModal({
  open,
  onClose,
  offboardingId,
  source,
  sectionType,
  title,
  isAcknowledged = false,
  onAcknowledgeSuccess,
}: ViewClearanceFormModalProps) {
  const isSqe = useSqeLabs();
  const useSqeApi = source === "SQELABS" || (source == null && isSqe);
  const [acknowledging, setAcknowledging] = useState(false);
  const { questions, loading, error } = useOffboardingQuestionnaire(
    open ? offboardingId : null,
    sectionType,
    source,
  );
  const {
    detail,
    loading: detailLoading,
    error: detailError,
  } = useOffboardingDetail(
    open && sectionType === "IT_CLEARANCE" ? offboardingId : null,
    source,
  );
  const employeeId = detail?.offboarding.employeeId ?? null;
  const employeeName = detail?.offboarding.employeeName ?? undefined;

  const answers = useMemo(
    () => questions.map((q) => ({ answer: q.answer ?? "" })),
    [questions],
  );

  const answeredQuestions = useMemo<AnsweredQuestion[]>(
    () =>
      questions
        .map((question, index) => ({
          question,
          answer: question.answer?.trim() ?? "",
          questionIndex: index,
        }))
        .filter(
          ({ answer, questionIndex }) =>
            answer !== "" &&
            shouldShowOffboardingQuestion(
              questions,
              questionIndex,
              answers,
              sectionType,
            ),
        ),
    [answers, questions, sectionType],
  );

  const renderAnsweredQuestion = (
    { question, answer }: AnsweredQuestion,
    displayIndex: number,
  ) => (
    <OffboardingQuestionRenderer
      question={question}
      readOnly={true}
      index={displayIndex}
      field={{
        name: `q-${question.questionId}`,
        state: {
          value: answer,
          meta: {},
        },
        handleChange: () => {},
        handleBlur: () => {},
      }}
    />
  );

  const renderAnswers = () => {
    if (sectionType !== "IT_CLEARANCE") {
      return answeredQuestions.map((item, index) => (
        <Fragment key={item.question.questionId}>
          {renderAnsweredQuestion(item, index + 1)}
        </Fragment>
      ));
    }

    let displayIndex = 0;

    const groups = useSqeApi ? SQE_IT_CLEARANCE_GROUPS : IT_CLEARANCE_GROUPS;
    return groups.map((group) => {
      const groupQuestions = answeredQuestions.filter(
        (item) =>
          item.questionIndex >= group.start && item.questionIndex <= group.end,
      );

      if (groupQuestions.length === 0) return null;

      return (
        <section key={group.title} className={styles.answerGroup}>
          <div className={styles.sectionHeader}>
            <h3 className="heading-sm">{group.title}</h3>
          </div>
          <div className={styles.groupContent}>
            {groupQuestions.map((item) => {
              displayIndex += 1;

              return (
                <Fragment key={item.question.questionId}>
                  {renderAnsweredQuestion(item, displayIndex)}
                </Fragment>
              );
            })}
          </div>
        </section>
      );
    });
  };

  useEffect(() => {
    if (error) showError(error);
  }, [error]);

  useEffect(() => {
    if (detailError) showError(detailError);
  }, [detailError]);

  const handleAcknowledge = async () => {
    if (isAcknowledged) return;

    setAcknowledging(true);

    try {
      await postApi(
        useSqeApi ? `${SQE_OFFBOARDING_URL}/acknowledged/${offboardingId}` : `${API_OFFBOARDING_ACKNOWLEDGED}/${offboardingId}`,
        accessToken(),
        {},
      );
      showSuccess("Exit interview acknowledged successfully.");
      onAcknowledgeSuccess?.();
      onClose();
    } catch (error) {
      showError(error instanceof Error ? error.message : "Unable to acknowledge exit interview.");
    } finally {
      setAcknowledging(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        if (acknowledging) return;
        onClose();
      }}
      title={title}
      size="xl"
    >
      {(loading || detailLoading || acknowledging) && <Spinner />}
      {!loading && !detailLoading && questions.length === 0 && (
        <p className="body-md">No questions available.</p>
      )}
      {!loading && !detailLoading && questions.length > 0 && (
        <div className={styles.content}>
          {sectionType === "IT_CLEARANCE" && employeeId != null ? (
            <DeallocatedAssetsTable
              employeeId={employeeId}
              employeeName={employeeName}
            />
          ) : null}
          {answeredQuestions.length > 0 ? (
            renderAnswers()
          ) : (
            <p className="body-md">No answers available.</p>
          )}
          {sectionType === "EXIT_INTERVIEW" ? (
            <div className={styles.acknowledgementPanel}>
              <div className={styles.acknowledgementText}>
                <p className="body-sm">
                  {isAcknowledged
                    ? "This exit interview form has been acknowledged."
                    : "Confirm that you have reviewed and acknowledged the submitted exit interview form."}
                </p>
                <span
                  className={
                    isAcknowledged
                      ? styles.acknowledgedStatus
                      : styles.pendingStatus
                  }
                >
                  {isAcknowledged ? "Acknowledged" : "Acknowledgement pending"}
                </span>
              </div>
              <Button
                variant="success"
                startIcon={
                  <CheckCircle2 size={16} strokeWidth={2} aria-hidden />
                }
                onClick={() => {
                  void handleAcknowledge();
                }}
                disabled={acknowledging || isAcknowledged}
              >
                {isAcknowledged
                  ? "Acknowledged"
                  : acknowledging
                    ? "Acknowledging..."
                    : "Acknowledge"}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
